// Web Push subscription helpers — frontend half of the push pipeline.
//
// Flow:
//   1. App boots → ensureSWReady → maybeSubscribe()
//   2. If user has not yet decided (Notification.permission === "default")
//      we DON'T auto-prompt; we let the user click an explicit
//      "Enable notifications" button (in the header bell menu, eventually
//      in settings). That avoids the dreaded "site is asking to send
//      notifications" prompt on first visit.
//   3. On request:
//      - Notification.requestPermission()
//      - Fetch VAPID public key
//      - PushManager.subscribe({ userVisibleOnly: true, applicationServerKey })
//      - POST subscription to /api/push/subscribe
//
// Idempotent: re-subscribing returns the existing subscription server-side.

import { api, formatApiError } from "./api";
import { getServiceWorkerReady } from "./pwa";
import { logger } from "./logger";

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
}

export function pushSupported() {
    return typeof window !== "undefined"
        && "serviceWorker" in navigator
        && "PushManager" in window
        && "Notification" in window;
}

export async function getCurrentSubscription() {
    if (!pushSupported()) return null;
    const reg = await getServiceWorkerReady();
    if (!reg) return null;
    return reg.pushManager.getSubscription();
}

export async function subscribeToPush() {
    if (!pushSupported()) throw new Error("Push notifications are not supported in this browser.");
    const reg = await getServiceWorkerReady();
    if (!reg) throw new Error("Service worker is not ready.");

    // Request OS-level permission. Bail if denied — Chrome blocks future
    // calls until the user manually re-enables in site settings.
    if (Notification.permission === "denied") {
        throw new Error("Notifications were blocked. Re-enable them in your browser site settings.");
    }
    if (Notification.permission !== "granted") {
        const result = await Notification.requestPermission();
        if (result !== "granted") {
            throw new Error("Permission not granted.");
        }
    }

    // Fetch VAPID public key (server side never changes it — could cache,
    // but the call is tiny and avoids stale-key bugs).
    let pubKey;
    try {
        const { data } = await api.get("/push/vapid-public-key");
        pubKey = data.public_key;
    } catch (e) {
        throw new Error(formatApiError(e?.response?.data?.detail) || e.message || "Failed to fetch push key");
    }
    if (!pubKey) throw new Error("Server has no VAPID public key configured.");

    // Subscribe (or get existing). userVisibleOnly:true is required by
    // Chrome — every push MUST surface a notification.
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(pubKey),
        });
    }

    // POST raw JSON; the SubscriptionJSON shape (endpoint + keys.{p256dh,auth})
    // is what pywebpush expects on the server.
    const json = sub.toJSON();
    try {
        await api.post("/push/subscribe", {
            endpoint: json.endpoint,
            keys: json.keys,
        });
    } catch (e) {
        throw new Error(formatApiError(e?.response?.data?.detail) || e.message || "Failed to register subscription");
    }
    logger.info("[push] subscribed", json.endpoint);
    return sub;
}

export async function unsubscribeFromPush() {
    const sub = await getCurrentSubscription();
    if (!sub) return;
    try {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
    } catch (e) {
        logger.warn("[push] server unsubscribe failed", e);
    }
    await sub.unsubscribe();
}
